import { supabase } from "@/integrations/supabase/client";
import { detectBrowser } from "@/lib/gtmDataLayer";

export type CheckoutClickEvent =
  | "banner_shown"
  | "banner_dismissed"
  | "checkout_click"
  | "checkout_redirect"
  | "checkout_fallback"
  | "checkout_retry"
  | "checkout_error";

interface LogArgs {
  event: CheckoutClickEvent;
  cartId?: string | null;
  cartValue?: number;
  itemCount?: number;
  meta?: Record<string, unknown>;
}

/**
 * Events that immediately navigate away from the page. For these we cannot
 * use the regular Supabase JS client — the fetch is cancelled when the
 * browser unloads. Use `fetch(..., { keepalive: true })` (or sendBeacon)
 * directly to PostgREST so the log row survives the redirect.
 */
const NAVIGATION_EVENTS: ReadonlySet<CheckoutClickEvent> = new Set([
  "checkout_click",
  "checkout_redirect",
  "checkout_fallback",
  "checkout_retry",
  "checkout_error",
]);

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

function sendNavigationLog(payload: Record<string, unknown>): void {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;
  const url = `${SUPABASE_URL}/rest/v1/checkout_click_log`;
  const body = JSON.stringify([payload]);

  // Prefer fetch keepalive (supports headers, ≤64KB body). Fall back to
  // sendBeacon if fetch isn't available (very old browsers / SSR).
  if (typeof fetch === "function") {
    try {
      void fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          Prefer: "return=minimal",
        },
        body,
      });
      return;
    } catch {
      // fall through to sendBeacon
    }
  }

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      // sendBeacon cannot set headers; PostgREST therefore rejects this with
      // 401 in normal circumstances. Kept only as last-resort signal so we
      // at least see the request in CF logs.
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } catch {
      // give up silently
    }
  }
}

/**
 * Fire-and-forget logger for checkout-flow click telemetry.
 * Used by the "Till betalning är åtgärdat" banner and the checkout button
 * so we can verify in real-time that customers are clicking AND making it
 * through to Shopify.
 *
 * Writes to public.checkout_click_log (anon insert allowed, admin read only).
 * Never throws — logging must never block the actual checkout flow.
 *
 * Navigation events (`checkout_click`, `checkout_redirect`, etc.) use
 * `fetch keepalive` so the request survives page unload/redirect. Non-
 * navigation events (`banner_shown`, `banner_dismissed`) go through the
 * regular Supabase client.
 */
export function logCheckoutClick(args: LogArgs): void {
  try {
    const payload = {
      event: args.event,
      cart_id: args.cartId ?? null,
      cart_value: args.cartValue ?? null,
      item_count: args.itemCount ?? null,
      browser: detectBrowser(),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      meta: args.meta ?? {},
    };

    if (NAVIGATION_EVENTS.has(args.event)) {
      sendNavigationLog(payload);
      return;
    }

    void supabase.from("checkout_click_log").insert([payload as never]);
  } catch {
    // never block checkout on telemetry
  }
}
